"use strict";

import {
  GetRowIdParams,
  IServerSideDatasource,
  IServerSideGetRowsRequest,
} from "ag-grid-enterprise";
import {
  ColDef,
  ColGroupDef,
  DefaultMenuItem,
  GetContextMenuItemsParams,
  GridReadyEvent,
  MenuItemDef,
} from "ag-grid-community";
import { AgGridReact, CustomCellRendererProps } from "ag-grid-react";
import React, { useCallback, useEffect, useMemo } from "react";
import { useRef, useState } from "react";
import {
  paginationPageSize,
  paginationPageSizeSelector,
  repositoryPos,
} from "../../../_base/_const/_constVar";
import { MessageResponse } from "../../../model/MessageResponse";
import { randomId } from "@mantine/hooks";
import { AG_GRID_LOCALE_VN } from "@ag-grid-community/locale";
import { Badge, Text } from "@mantine/core";
import { getValueById } from "../../../_base/helper/FunctionHelper";
import CustomFilterTypePromotion from "./CustomFilterTypePromotion";
import { formatDateTransfer } from "../../../common/FormatDate/FormatDate";
import {
  getTblDepartmentSelect,
  getTblDmEmployeeAll,
} from "../../../service/getSelectApi";
import CustomFilterEmployee from "./CustomFilterEmployee";
import CustomFilterStatus from "./CustomFilterStatus";
import CustomFilterDepartment from "./CustomFilterDepartment";
import CustomFilterCodeEmp from "./CustomFilterCodeEmp";

const PromotionReport = () => {
  const [loading, setLoading] = useState(true);
  const gridRef = useRef<AgGridReact<any>>(null);
  const [height, setHeight] = useState(900);
  const headerRef = React.useRef<HTMLDivElement>(null);

  const containerStyle = useMemo(() => ({ width: "100%", height: height }), []);
  const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);

  const typePromotion = [
    {
      label: "CTKM thông thường",
      value: "1",
    },
    {
      label: "Flash Sale",
      value: "2",
    },
    {
      label: "Deal sốc",
      value: "4",
    },
  ];

  const [dataEmployee, setDataEmployee] = useState<any[]>([]);

  const fetchDataEmp = async () => {
    const res = await getTblDmEmployeeAll();

    if (res) {
      const data = res
        .filter((item) => item.id && item.fullname)
        .map((item) => ({
          label: `${item.code} - ${item.fullname}`,
          value: String(item.id),
        }));
      setDataEmployee(data);
    }
  };

  const fetchData = async (request: IServerSideGetRowsRequest) => {
    try {
      const queryParams = {
        Request: request,
      };

      return await repositoryPos.post<MessageResponse<any[]>>(
        "/api/v1/TblPromotion/get-promotion-report",
        queryParams
      );
    } catch (error) {
      console.error(error);
    }
  };

  const getServerSideDataSource: () => IServerSideDatasource = () => {
    return {
      getRows: async (params) => {
        var response = await fetchData(params.request);
        // adding delay to simulate real server call

        if (response && response.success) {
          params.success({
            rowData: response.data,
            rowCount: response.totalCount,
          });
          console.log(response.data);
          setLoading(false);
        } else {
          // inform the grid request failed
          params.fail();
          setLoading(false);
        }
      },
    };
  };

  const getRowId = useCallback((params: GetRowIdParams) => {
    if (params.data.id != null) {
      return "leaf-" + randomId();
    }
    const rowGroupCols = params.api.getRowGroupColumns();
    const rowGroupColIds = rowGroupCols.map((col) => col.getId()).join("-");
    const thisGroupCol = rowGroupCols[params.level];
    return (
      "group-" +
      rowGroupColIds +
      "-" +
      (params.parentKeys || []).join("-") +
      params.data[thisGroupCol.getColDef().field!]
    );
  }, []);

  const filterParamsDefault = {
    buttons: ["clear", "reset", "apply"],
    debounceMs: 1000,
    maxNumConditions: 5,
  };

  const getColorStatus = (
    status: string,
    effDate: Date,
    expDate: Date
  ): string => {
    const now = new Date();
    const effectiveDate = effDate ? new Date(effDate) : null;
    const expirationDate = expDate ? new Date(expDate) : null;
    switch (status) {
      case "2": // Đã duyệt
        if (effectiveDate && expirationDate) {
          if (now >= effectiveDate && now <= expirationDate) {
            return "teal"; // Đang chạy
          } else if (now > expirationDate) {
            return "gray"; // Hết hiệu lực
          } else if (now < effectiveDate) {
            return "teal.4"; // Hết hiệu lực
          }
          return "teal";
        }
        return "blue"; // Đang xét duyệt
      case "3": // Dừng duyệt
        return "yellow"; // Đang xét duyệt (có thể chỉnh sửa, không cho xóa)
      case "4": // Từ chối
        return "red"; // Không áp dụng (có thể sửa, không cho xóa)
      case "5": // Hủy duyệt
        return "blue"; // Chưa áp dụng (có thể sửa, không cho xóa)
      default: // Chưa duyệt
        return "blue"; // Đang xét duyệt (có thể chỉnh sửa, có thể xóa)
    }
  };

  const getStatus = (text: any) => {
    switch (text) {
      case "2":
        return "Đã duyệt";
      case "3":
        return "Tạm dừng";
      case "4":
        return "Từ chối";
      case "5":
        return "Hủy duyệt";
      default:
        return "Chưa duyệt";
    }
  };

  const columnDefs = useMemo<(ColDef | ColGroupDef)[]>(
    () => [
      {
        field: "code",
        headerName: "Mã CTKM",
        width: 175,
        filter: "agTextColumnFilter",
        cellRenderer: (params: CustomCellRendererProps) => {
          return (
            <Badge variant="dot" color="green">
              {params.value}
            </Badge>
          );
        },
      },
      {
        field: "type",
        headerName: "Chính sách khuyến mãi",
        width: 250,
        filter: CustomFilterTypePromotion,
        floatingFilterComponent: CustomFilterTypePromotion,
        cellRenderer: (params: CustomCellRendererProps) => {
          return (
            <Text mt={10}>
              {getValueById(String(params.value), typePromotion, "label")}
            </Text>
          );
        },
      },
      {
        field: "note",
        headerName: "Tên CTKM",
        width: 400,
        filter: "agTextColumnFilter",
      },

      // --- Nhóm thông tin ngày tháng ---
      {
        field: "createDate",
        headerName: "Ngày tạo",
        width: 200,
        filter: "agDateColumnFilter",
        cellRenderer: (params: CustomCellRendererProps) => {
          return (
            <Text mt={10}>
              {params.value && formatDateTransfer(params.value)}
            </Text>
          );
        },
      },
      {
        field: "effDate",
        headerName: "Ngày hiệu lực",
        width: 200,
        filter: "agDateColumnFilter",
        cellRenderer: (params: CustomCellRendererProps) => {
          return (
            <Text mt={10}>
              {params.value && formatDateTransfer(params.value)}
            </Text>
          );
        },
      },
      {
        field: "expDate",
        headerName: "Ngày hết hạn",
        width: 200,
        filter: "agDateColumnFilter",
        cellRenderer: (params: CustomCellRendererProps) => {
          return (
            <Text mt={10}>
              {params.value && formatDateTransfer(params.value)}
            </Text>
          );
        },
      },
      {
        field: "approveDate",
        headerName: "Ngày duyệt",
        width: 200,
        filter: "agDateColumnFilter",
        cellRenderer: (params: CustomCellRendererProps) => {
          return (
            <Text mt={10}>
              {params.value && formatDateTransfer(params.value)}
            </Text>
          );
        },
      },

      // --- Nhóm thông tin duyệt ---
      {
        field: "approveBy",
        headerName: "Người duyệt",
        width: 275,
        filter: CustomFilterEmployee,
        floatingFilterComponent: CustomFilterEmployee,
        cellRenderer: (params: CustomCellRendererProps) => {
          return (
            <Text mt={10}>
              {getValueById(String(params.value), dataEmployee, "label")}
            </Text>
          );
        },
      },
      {
        field: "approveStatus",
        headerName: "Trạng thái duyệt",
        filter: CustomFilterStatus,
        floatingFilterComponent: CustomFilterStatus,
        width: 250,
        cellRenderer: (params: CustomCellRendererProps) => {
          return (
            <Badge
              color={getColorStatus(
                params.data.approveStatus,
                params.data.effDate,
                params.data.expDate
              )}
            >
              {getStatus(params.data.approveStatus)}
            </Badge>
          );
        },
      },

      // --- Nhóm thông tin sản phẩm ---
      {
        field: "itemCode",
        headerName: "Mã hàng hóa",
        width: 150,
        filter: "agTextColumnFilter",
      },
      {
        field: "itemName",
        headerName: "Tên hàng hóa",
        width: 400,
        filter: "agTextColumnFilter",
      },
      {
        field: "quality",
        headerName: "Số lượng",
        width: 125,
        filter: "agNumberColumnFilter",
      },

      // --- Nhóm thông tin giá & chiết khấu ---
      {
        field: "priceOnPriceList",
        headerName: "Giá bán lẻ",
        width: 200,
        filter: "agNumberColumnFilter",
        cellRenderer: (params: CustomCellRendererProps) => {
          return (
            <Text mt={10}>
              {params.value || String(params.value) === "0"
                ? Number(params.value).toLocaleString()
                : ""}
            </Text>
          );
        },
      },
      {
        field: "discountAmount",
        headerName: "Khuyến mãi",
        width: 200,
        filter: "agNumberColumnFilter",
        cellRenderer: (params: CustomCellRendererProps) => {
          return (
            <Text mt={10}>
              {params.value || String(params.value) === "0"
                ? Number(params.value).toLocaleString()
                : ""}
            </Text>
          );
        },
      },
      {
        field: "discountPercent",
        headerName: "% Chiết khấu",
        width: 200,
        filter: "agNumberColumnFilter",
        cellRenderer: (params: CustomCellRendererProps) => {
          return (
            <Text mt={10}>
              {params.value || String(params.value) === "0"
                ? `${params.value} %`
                : ""}
            </Text>
          );
        },
      },
      {
        field: "discountAdded",
        headerName: "Chiết khấu trên đơn",
        width: 200,
        filter: "agNumberColumnFilter",
        cellRenderer: (params: CustomCellRendererProps) => {
          return (
            <Text mt={10}>
              {params.value || String(params.value) === "0"
                ? Number(params.value).toLocaleString()
                : ""}
            </Text>
          );
        },
      },
      {
        field: "ohDiscountAmount",
        headerName: "Tiền chiết khấu khác",
        width: 200,
        filter: "agNumberColumnFilter",
        cellRenderer: (params: CustomCellRendererProps) => {
          return (
            <Text mt={10}>
              {params.value || String(params.value) === "0"
                ? Number(params.value).toLocaleString()
                : ""}
            </Text>
          );
        },
      },
      {
        field: "priceFinal",
        headerName: "Tiền sau giảm giá",
        width: 200,
        filter: "agNumberColumnFilter",
        cellRenderer: (params: CustomCellRendererProps) => {
          return (
            <Text mt={10}>
              {params.value || String(params.value) === "0"
                ? Number(params.value).toLocaleString()
                : ""}
            </Text>
          );
        },
      },
      {
        field: "totalLineAmount",
        headerName: "Thành tiền",
        width: 200,
        filter: "agNumberColumnFilter",
        cellRenderer: (params: CustomCellRendererProps) => {
          return (
            <Text mt={10}>
              {params.value || String(params.value) === "0"
                ? Number(params.value).toLocaleString()
                : ""}
            </Text>
          );
        },
      },

      // --- Nhóm thông tin đơn hàng & Sales ---
      {
        field: "orderNumber",
        headerName: "Mã đơn hàng",
        width: 175,
        filter: "agTextColumnFilter",
        cellRenderer: (params: CustomCellRendererProps) => {
          return params.value ? (
            <Badge color="blue" variant="dot">
              {params.value}
            </Badge>
          ) : (
            <></>
          );
        },
      },
      {
        field: "orderDate",
        headerName: "Ngày đặt hàng",
        width: 200,
        filter: "agDateColumnFilter",
        cellRenderer: (params: CustomCellRendererProps) => {
          return (
            <Text mt={10}>
              {params.value && formatDateTransfer(params.value)}
            </Text>
          );
        },
      },
      {
        field: "saleCode",
        headerName: "Người hưởng số",
        width: 275,
        filter: CustomFilterCodeEmp,
        floatingFilterComponent: CustomFilterCodeEmp,
        cellRenderer: (params: CustomCellRendererProps) => {
          return (
            <Text mt={10}>
              {[params.value, params.data.saleName]
                .filter((i) => i && String(i).length > 0)
                .join(" - ")}
            </Text>
          );
        },
      },
      {
        field: "deparmentCode",
        headerName: "Phòng ban",
        width: 250,
        filter: CustomFilterDepartment,
        floatingFilterComponent: CustomFilterDepartment,
        cellRenderer: (params: CustomCellRendererProps) => {
          return <Text mt={10}>{params.data.name}</Text>;
        },
      },
    ],
    [typePromotion, dataEmployee]
  );

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      autoHeaderHeight: true,
      allowedAggFuncs: ["sum", "min", "max", "avg"],
      filter: true,
      enableRowGroup: true,
      enableValue: true,
      floatingFilter: true,
      floatingFilterComponentParams: {
        showDisabledCheckboxes: true,
      },
      filterParams: filterParamsDefault,
    };
  }, []);

  const defaultColGroupDef = useMemo<Partial<ColGroupDef>>(() => {
    return {
      headerName: "A shared prop for all Groups",
    };
  }, []);

  const onGridReady = useCallback(async (params: GridReadyEvent) => {
    var datasource = getServerSideDataSource();
    // register the datasource with the grid
    params.api!.setGridOption("serverSideDatasource", datasource);
  }, []);

  // const detailCellRendererParams = {
  //   detailGridOptions: {
  //     columnDefs: [
  //       { field: "id" },
  //       { field: "itemCode" },
  //       { field: "itemId" },
  //       { field: "lineTyle" },
  //       { field: "primaryUomCode" },
  //       { field: "subInvId" },
  //       { field: "totalLineAmount" },
  //       { field: "priceFinal" },
  //       { field: "quality" },
  //     ],
  //     defaultColDef: {
  //       suppressHeaderMenuButton: true,
  //       suppressHeaderContextMenu: true,
  //       autoHeaderHeight: true,
  //     },
  //   },
  //   getDetailRowData: async (params: any) => {
  //     // supply data to the detail grid
  //     const data = await repositoryPos.get<MessageResponse<any[]>>(
  //       "/api/v1/CreateSellItem/details-order?orderNumber=" +
  //         params.data.orderNumber
  //     );
  //     if (data && data.data) params.successCallback(data?.data);
  //   },
  // };

  const statusBar = useMemo(
    () => ({
      statusPanels: [
        { statusPanel: "agTotalAndFilteredRowCountComponent" },
        { statusPanel: "agTotalRowCountComponent" },
        { statusPanel: "agFilteredRowCountComponent" },
        { statusPanel: "agSelectedRowCountComponent" },
        { statusPanel: "agAggregationComponent" },
      ],
    }),
    []
  );

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
      var result: (DefaultMenuItem | MenuItemDef)[] = [
        "cut",
        "copy",
        "copyWithHeaders",
        "copyWithGroupHeaders",
        "paste",
        "separator",
        "chartRange",
        "export",
      ];
      return result;
    },
    [window]
  );

  useEffect(() => {
    fetchDataEmp();
  }, []);

  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 800;
    const handleResize = () => {
      // 160 là chiều cao của phần phân trang
      // headerHeight là chiều cao của phần header
      setHeight(window.innerHeight - (200 + headerHeight));
    };

    handleResize(); // Set initial height
    window.addEventListener("resize", handleResize); // Update height on window resize

    return () => {
      window.removeEventListener("resize", handleResize); // Clean up event listener
    };
  }, []);

  return (
    <div style={containerStyle}>
      <div style={gridStyle} className={"ag-theme-quartz"}>
        <AgGridReact<any>
          ref={gridRef}
          loading={loading}
          suppressNoRowsOverlay
          columnDefs={columnDefs}
          defaultColGroupDef={defaultColGroupDef}
          defaultColDef={defaultColDef}
          rowModelType={"serverSide"}
          pagination={true}
          paginationPageSize={paginationPageSize}
          paginationPageSizeSelector={paginationPageSizeSelector}
          cacheBlockSize={paginationPageSize}
          maxBlocksInCache={10}
          onGridReady={onGridReady}
          localeText={AG_GRID_LOCALE_VN}
          sideBar={true}
          enableAdvancedFilter={false}
          masterDetail={true}
          // detailCellRendererParams={detailCellRendererParams}
          rowSelection={"multiple"}
          suppressAggFuncInHeader={true}
          getRowId={getRowId}
          suppressRowClickSelection={true}
          groupSelectsChildren={true}
          blockLoadDebounceMillis={200}
          suppressServerSideFullWidthLoadingRow={false}
          maxConcurrentDatasourceRequests={1}
          animateRows={true}
          rowBuffer={paginationPageSize}
          purgeClosedRowNodes={true}
          pivotPanelShow={"always"}
          rowGroupPanelShow={"always"}
          statusBar={statusBar}
          enableRangeSelection
          enableCharts
          allowContextMenuWithControlKey={true}
          getContextMenuItems={getContextMenuItems}
          suppressColumnVirtualisation
          paginateChildRows={true}
          paginationAutoPageSize={true}
        />
      </div>
    </div>
  );
};

export default PromotionReport;
